<script lang="ts">
	import { hex_to_decimal, capFirstChar } from 'src/helpers/utilities';
	import { presenceStore, type PresenceData } from 'src/components/Radar/stores/presence';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import Radar from './components/Radar.svelte';
	import {
		Button,
		Col,
		FlexibleCard,
		Modal,
		ParameterCardGroup,
		ParameterCardGroupV2,
		Row,
		Text,
		Wizard,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	import { startRadarRequest } from './helpers/requests';
	import { onDestroy, onMount } from 'svelte';
	import RadarSetupWizard from './RadarSetupWizard.svelte';
	import PlaceDeviceStep from './components/Items/Steps/PlaceDeviceStep.svelte';
	import ConfigStep from './components/Items/Steps/ConfigStep.svelte';
	import RoomStep from './components/Items/Steps/RoomStep.svelte';
	import ZonesStep from './components/Items/Steps/ZonesStep.svelte';
	import FurnituresStep from './components/Items/Steps/FurnituresStep.svelte';
	import FinishStep from './components/Items/Steps/FinishStep.svelte';
	import RadarLastEvents from './components/Items/RadarLastEvents.svelte';
	import { rgbToCss, zoneEnabledFromDevice } from './helpers/radar-helper';
	import { getIlluminationIcon } from 'src/helpers/device_data_helpers';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	export let device: any;
	export let settings: any;
	export let status: any;

	let showModal = false;
	let showSetUpWizard = false;
	let showSettings = false;
	let interval: NodeJS.Timeout;
	let presenceData: PresenceData | undefined;
	let unsubscribe: () => void;

	interface SimulatedPoint {
		id: string;
		energetic?: boolean;
		color?: string;
	}

	let simulatedPoints: SimulatedPoint[] = [];

	$: radarPoints = presenceStore.getDeviceData(hex_to_decimal(device.id));
	$: presenceConfig = settings.presence || {};

	// Extract room boundaries from main zone and blind spots from presence config
	$: mainZoneKey = presenceConfig?.main_zone;
	$: roomBoundaries = mainZoneKey && settings[mainZoneKey] ? settings[mainZoneKey].area || [] : [];
	$: blindSpots = presenceConfig?.blind || [];

	// Include all zones (including main zone) in zones list, sorted by zone ID
	$: zonesData = Object.keys(settings)
		.filter((key) => key.startsWith('presencezone:'))
		.sort((a, b) => parseInt(a.split(':')[1]) - parseInt(b.split(':')[1]))
		.map((key) => {
			const raw = settings[key] || {};
			const idFromKey = parseInt(key.split(':')[1], 10);
			return {
				...raw,
				id: raw.id != null ? raw.id : idFromKey,
				enabled: zoneEnabledFromDevice(raw),
				visible: true,
				isMainZone: key === mainZoneKey,
			};
		});

	$: displayInfo = (() => {
		const presenceCount = presenceData?.presenceObjects || 0;
		const hasPresence = presenceCount > 0;
		let label: string;
		if (!hasPresence) {
			label = $format('presence_label_no_objects', { default: 'No entities' });
		} else if (presenceCount === 1) {
			label = $format('presence_label_object', { default: 'Object' });
		} else {
			label = $format('presence_label_objects', { default: 'Objects' });
		}
		return { count: String(presenceCount), hasPresence, label };
	})();
	$: populatedZones = (() => {
		const allZones = Object.keys(status).filter((key) => key.startsWith('presencezone:'));
		const mainZoneKey = 'presencezone:200';

		// If only main zone exists, check its value
		if (allZones.length === 1 && allZones[0] === mainZoneKey) {
			return status[mainZoneKey]?.value ? 1 : 0;
		}

		// If multiple zones exist, count non-main zones with value=true
		return allZones.filter((key) => key !== mainZoneKey && status[key]?.value).length;
	})();

	// Extract and translate illumination value
	$: illumination = (() => {
		const value = status?.['illuminance:0']?.illumination || 'bright';
		const label = $format(`option_motion_mode_${value}`, {
			default: value.charAt(0).toUpperCase() + value.slice(1),
		});

		let icon = 'fa ' + getIlluminationIcon(status?.['illuminance:0']?.illumination);

		let iconColor;
		if (value === 'dark') {
			iconColor = '--color-primary';
		} else if (value === 'twilight') {
			iconColor = '--color-secondary';
		} else {
			iconColor = '--color-warning';
		}

		return { value, label, icon, iconColor };
	})();

	onMount(() => {
		subscribeToPresence();
		startRadarRequest(device);
		interval = setInterval(() => {
			startRadarRequest(device);
		}, 50000);
	});

	onDestroy(() => {
		if (interval) clearInterval(interval);
	});

	function subscribeToPresence() {
		if (unsubscribe) unsubscribe();
		const deviceId = hex_to_decimal(device.id);
		unsubscribe = presenceStore.subscribe((storeMap) => {
			presenceData = storeMap.get(deviceId);
			// Maintain backward compatibility with existing radar points logic
			radarPoints = presenceData?.objects || [];
		});
	}

	function startRadar() {
		startRadarRequest(device);
	}

	// ── Setup wizard state ───────────────────────────────────────────────────
	let wizardStepIdx = 0;

	$: wizardSteps = [
		{
			title: $format('presence_wizard_step_welcome', { default: 'Welcome' }),
			component: PlaceDeviceStep,
			nextLabel: $format('presence_wizard_btn_start', { default: "Let's start" }),
		},
		{
			title: $format('presence_wizard_step_config', { default: 'Sensor' }),
			component: ConfigStep,
			skippable: true,
		},
		{
			title: $format('presence_wizard_step_room', { default: 'Room' }),
			component: RoomStep,
		},
		{
			title: $format('presence_wizard_step_zones', { default: 'Zones' }),
			component: ZonesStep,
		},
		{
			title: $format('presence_wizard_step_furniture', { default: 'Furniture' }),
			component: FurnituresStep,
		},
		{
			title: $format('presence_wizard_step_finish', { default: 'Finish' }),
			component: FinishStep,
		},
	];

	$: isWizardFirst = wizardStepIdx === 0;
	$: isWizardLast = wizardStepIdx === wizardSteps.length - 1;
	$: currentWizardStep = wizardSteps[wizardStepIdx];

	function openWizard() {
		wizardStepIdx = 0;
		showSetUpWizard = true;
	}

	function closeWizard() {
		showSetUpWizard = false;
		wizardStepIdx = 0;
	}

	function goToWizardStep(idx: number) {
		if (idx >= 0 && idx < wizardSteps.length) wizardStepIdx = idx;
	}

	function nextWizardStep() {
		if (wizardStepIdx < wizardSteps.length - 1) wizardStepIdx += 1;
	}

	function prevWizardStep() {
		if (wizardStepIdx > 0) wizardStepIdx -= 1;
	}

	function handleWizardComplete(openEditor: boolean) {
		closeWizard();
		if (openEditor) showModal = true;
	}
</script>

<div
	class="d-flex flex-direction-column pa-m"
	class:justify-content-between={!$showNewLayout}
	class:height-full={!$showNewLayout}
>
	<div class="flex-grow-1">
		{#if $showNewLayout}
			<ParameterCardGroupV2
				class="mb-s"
				sectionParameters={[
					{
						title: String(populatedZones),
						icon: 'fat fa-vector-square',
						description: $format('presence_label_populated_zones', {
							default: 'Populated zones',
						}),
						iconColor: populatedZones > 0 ? '--color-success' : '--color-secondary',
					},
					{
						title: illumination.label,
						icon: mapIconToThin(illumination.icon),
						iconColor: illumination.iconColor,
						description: capFirstChar(
							$format('label_illuminance', { default: 'Illuminance' }),
						),
					},
					{
						title: displayInfo.count,
						icon: 'fat fa-person-simple',
						description: displayInfo.label,
						iconColor: displayInfo.hasPresence ? '--color-purple' : '--color-secondary',
					},
				]}
			/>
		{:else}
			<ParameterCardGroup
				class="mb-s"
				sectionParameters={[
					{
						title: String(populatedZones),
						icon: 'fas fa-vector-square',
						description: $format('presence_label_populated_zones', {
							default: 'Populated zones',
						}),
						iconColor: populatedZones > 0 ? '--color-success' : '--color-secondary',
					},
					{
						title: illumination.label,
						icon: illumination.icon,
						iconColor: illumination.iconColor,
						description: capFirstChar(
							$format('label_illuminance', { default: 'Illuminance' }),
						),
					},
					{
						title: displayInfo.count,
						icon: 'fas fa-person-simple',
						description: displayInfo.label,
						iconColor: displayInfo.hasPresence ? '--color-purple' : '--color-secondary',
					},
				]}
			/>
		{/if}
		<Row class="mt-m">
			<Col sm="12" md="12" lg="12">
				<Radar
					{radarPoints}
					{simulatedPoints}
					zones={zonesData}
					{device}
					{showSettings}
					config={presenceConfig}
					room={roomBoundaries}
					blind={blindSpots}
					{settings}
				/>
			</Col>
		</Row>

		{#each Array.from({ length: Math.ceil(zonesData.length / 2) }) as _, rowIndex}
			<Row>
				{#each zonesData.slice(rowIndex * 2, rowIndex * 2 + 2) as zone, zoneIndex}
					{@const zoneStatus = status[`presencezone:${zone.id}`]}
					{@const isLastRow = rowIndex === Math.ceil(zonesData.length / 2) - 1}
					{@const isOddTotal = zonesData.length % 2 === 1}
					{@const isOnlyCardInLastRow = isLastRow && isOddTotal && zoneIndex === 0}
					{@const objectCount = zoneStatus?.num_objects || 0}
					{@const mainLabel =
						objectCount === 1
							? $format('presence_label_total_objects', {
									values: { count: objectCount },
									default: `Total of ${objectCount} object`,
								})
							: $format('presence_label_total_objects_plural', {
									values: { count: objectCount },
									default: `Total of ${objectCount} objects`,
								})}
					{@const zoneLabel =
						objectCount === 1
							? $format('presence_label_has_objects', {
									values: { count: objectCount },
									default: `Has ${objectCount} object`,
								})
							: $format('presence_label_has_objects_plural', {
									values: { count: objectCount },
									default: `Has ${objectCount} objects`,
								})}
					{@const headerText = zone.isMainZone ? mainLabel : zoneLabel}
					<Col
						sm={isOnlyCardInLastRow ? '12' : '6'}
						md={isOnlyCardInLastRow ? '12' : '6'}
						lg={isOnlyCardInLastRow ? '12' : '6'}
					>
						<FlexibleCard
							backgroundColor={$showNewLayout ? '--color-0' : undefined}
							hideSlotRight
							hideSlotAdditional
							class="mt-s"
							header={headerText}
						>
							<div class="d-flex justify-content-start align-items-center">
								<div
									class="zones_color"
									style="background-color: {rgbToCss(zone.color)};"
								></div>
								<Text>{zone.name}</Text>
							</div>
						</FlexibleCard>
					</Col>
				{/each}
			</Row>
		{/each}
	</div>
	<Row class="mt-s">
		<Col sm="12" md="12" lg="12">
			<RadarLastEvents {device} {settings} />
		</Col>
	</Row>
	{#if !device?.shared}
		<Row class="mt-s">
			<Col sm="12" md="12" lg="12" class="mt-s">
				<div class="main-actions">
					{#if roomBoundaries.length === 0}
						<Button size="l" variant="secondary" on:click={openWizard}>
							<i class="fas fa-wand-magic-sparkles mr-xs" aria-hidden="true"></i>
							{$format('presence_button_setup_help', { default: 'Setup guide' })}
						</Button>
					{/if}
					<Button size="l" width="full" on:click={() => (showModal = true)}
						>{$format('presence_button_edit_zones', { default: 'Edit zones' })}</Button
					>
				</div>
			</Col>
		</Row>
	{/if}
</div>
<Modal
	on:close={() => (showModal = false)}
	{showModal}
	showCloseButton={false}
	title={device.name}
	backdropClose={false}
>
	<svelte:fragment slot="content">
		<Radar
			editable
			{radarPoints}
			{simulatedPoints}
			zones={zonesData}
			{device}
			bind:showModal
			{showSettings}
			config={presenceConfig}
			room={roomBoundaries}
			blind={blindSpots}
			{settings}
		/>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="d-flex flex-direction-row justify-content-between">
			<Button variant="secondary" size="m" on:click={openWizard}>
				<i class="fas fa-wand-magic-sparkles mr-xs" aria-hidden="true"></i>
				{$format('presence_button_setup_help', { default: 'Setup guide' })}
			</Button>
			<Button variant="secondary" size="m" on:click={() => (showModal = false)}
				>{$format('button_close', { default: 'Close' })}</Button
			>
		</div>
	</svelte:fragment>
</Modal>
{#if showSetUpWizard}
	<Modal
		on:close={closeWizard}
		title={$format('presence_modal_setup_wizard', { default: 'Setup guide' })}
		backdropClose={false}
		showModal={showSetUpWizard}
	>
		<svelte:fragment slot="toolbar">
			<Wizard class="mb-m">
				{#each wizardSteps as step, i (i)}
					<WizardItem
						on:click={() => goToWizardStep(i)}
						active={wizardStepIdx === i}
						title={step.title}
						tooltipTitle={step.title}
					/>
				{/each}
			</Wizard>
		</svelte:fragment>

		<svelte:fragment slot="content">
			<RadarSetupWizard
				{device}
				{settings}
				{roomBoundaries}
				{zonesData}
				currentStepIdx={wizardStepIdx}
				steps={wizardSteps}
				on:next={nextWizardStep}
			/>
		</svelte:fragment>

		<svelte:fragment slot="footer">
			<div class="wizard-footer">
				<div class="wizard-footer__back">
					{#if !isWizardFirst}
						<Button variant="secondary" size="m" on:click={prevWizardStep}>
							{$format('button_back', { default: 'Back' })}
						</Button>
					{/if}
				</div>
				<div class="wizard-footer__actions">
					{#if !isWizardLast && currentWizardStep?.skippable}
						<Button variant="secondary" size="m" on:click={nextWizardStep}>
							{$format('button_skip', { default: 'Skip' })}
						</Button>
					{/if}
					{#if !isWizardLast}
						<Button variant="primary" size="m" on:click={nextWizardStep}>
							{currentWizardStep?.nextLabel || $format('button_next', { default: 'Next' })}
						</Button>
					{:else}
						<Button
							variant="secondary"
							size="m"
							on:click={() => handleWizardComplete(false)}
						>
							{$format('button_done', { default: 'Done' })}
						</Button>
						<Button variant="primary" size="m" on:click={() => handleWizardComplete(true)}>
							{$format('presence_wizard_btn_open_editor', {
								default: 'Open zone editor',
							})}
						</Button>
					{/if}
				</div>
			</div>
		</svelte:fragment>
	</Modal>
{/if}

<style>
	:global(.modal-container > .modal-close-btn) {
		display: none !important;
	}

	/* Keep the presence modal body at a stable height so it never collapses or
	   jumps when switching between tabs (zones / furniture / config). */
	:global(.modal-container .modal-body) {
		min-height: 440px;
	}

	.main-actions {
		display: flex;
		gap: 8px;
		width: 100%;
	}

	.wizard-footer {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	.wizard-footer__back {
		min-width: 80px;
	}

	.wizard-footer__actions {
		display: flex;
		flex-direction: row;
		gap: 8px;
	}

	.zones_color {
		width: 24px;
		height: 24px;
		border-radius: 4px;
		margin-right: 10px;
	}
</style>
